import Alpine from "alpinejs";

import "./carousel.css";

type State = {
  index: number;
  progress: number;
  itemCount: number;
  itemsInView: number;
  scrollTimeout: ReturnType<typeof setTimeout>;
  observer: MutationObserver | null;
  isStart: boolean;
  isEnd: boolean;
};

type Config = {
  initialIndex: State["index"];
  shouldLoop: boolean;
  shouldAutoplay: boolean;
};

const defaultConfig: Config = {
  initialIndex: 0,
  shouldLoop: false,
  shouldAutoplay: false,
};

// How close to a value in pixels should we consider it to be the target
const SCROLL_UPDATE_TOLERANCE = 10;

// How long should we wait before we consider the scroll to have stopped
const SCROLL_TIMEOUT_DURATION = 100;

Alpine.data("carousel", (config: Config = defaultConfig) => {
  const state: State = {
    index: config.initialIndex,
    progress: 0,
    itemCount: 0,
    itemsInView: 1,
    scrollTimeout: setTimeout(() => {}, 0),
    observer: null,
    isStart: true,
    isEnd: false,
  };

  return {
    // State
    ...config,
    ...state,
    // State Modifiers
    handleNext() {
      this.index = this.calculateNext();
    },
    handlePrevious() {
      this.index = this.calculatePrevious();
    },
    handleReset() {
      this.index = this.initialIndex;
    },

    // Utility Methods
    calculateNext() {
      if (this.shouldLoop) return (this.index + 1) % this.itemCount;
      else return Math.min(this.index + 1, this.itemCount - 1);
    },
    calculatePrevious() {
      if (this.shouldLoop) return (this.index - 1) % this.itemCount;
      else return Math.max(this.index - 1, 0);
    },

    // Event Handlers
    handleIndexChange() {
      const items = this.$refs.items;
      const target = items.children[this.index] as HTMLElement;

      this.progress = ((this.index + 1) / this.itemCount) * 100;

      this.isStart = this.index === 0;
      this.isEnd = this.index > this.itemCount - this.itemsInView;

      this.$refs.items.scrollTo({
        left: target.offsetLeft,
        behavior: "smooth",
      });
    },

    handleScrollEnd() {
      const children = Array.from(this.$refs.items.children) as HTMLElement[];

      // Iterate through each child and check if it's close enough to the edge to find 'active index'
      children.every((child, index) => {
        const distanceOfChildFromEdge = Math.abs(child.offsetLeft - this.$refs.items.scrollLeft);
        if (distanceOfChildFromEdge > SCROLL_UPDATE_TOLERANCE) return true;
        this.index = index;
        return false;
      });
    },

    /**
     * Reloads alpine model. This is used when we have data loaded in post page hydration.
     * i.e React fetching the children components.
     */

    // Lifecycle Hooks
    init() {
      this.$watch("index", this.handleIndexChange.bind(this));

      this.observer = new MutationObserver(([mutation]) => {
        if (mutation.type === "childList") {
          this.itemCount = this.$refs.items.childElementCount;

          const itemWidth = this.$refs.items.firstElementChild?.clientWidth ?? this.$refs.items.clientWidth;
          this.itemsInView = this.$refs.items.clientWidth / itemWidth;
        }
      });
    },

    // Bound Directives
    next: {
      ["@click"]() {
        this.handleNext();
      },
      [":disabled"]() {
        return this.isEnd;
      },
    },
    previous: {
      ["@click"]() {
        this.handlePrevious();
      },
      [":disabled"]() {
        return this.isStart;
      },
    },
    progressBar: {
      [":style"]() {
        return `--progress: ${this.progress}%`;
      },
    },
    items: {
      ["x-ref"]: "items",
      ["@scroll"]() {
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(this.handleScrollEnd.bind(this), SCROLL_TIMEOUT_DURATION);
      },
      ["x-init"]() {
        this.itemCount = this.$refs.items.childElementCount;

        const itemWidth = this.$refs.items.firstElementChild?.clientWidth ?? this.$refs.items.clientWidth;
        this.itemsInView = this.$refs.items.clientWidth / itemWidth;

        if (!this.observer) return;
        this.observer.observe(this.$refs.items, {
          childList: true,
        });
      },
    },
    ["total"]: {
      ["x-text"]() {
        const count = parseInt((this.$el.dataset.total as string) ?? "0");
        if (count > 9) return count;
        return "0".concat(count.toString());
      },
    },
    ["display_index"]: {
      ["x-text"]() {
        const count = this.index + 1;
        if (count > 9) return count;
        return "0".concat(count.toString());
      },
    },
  };
});
